import { useMemo } from 'react'
import type { JavascriptDialogStates } from '../types'

interface UseJavascriptUIParams {
  openDialog: (dialogName: keyof JavascriptDialogStates) => void
  handleStartIntro: (steps: any[]) => void
  isProcessing: boolean
  hasError: boolean
  hasResult: boolean
  hasExecutionResult: boolean
}

/**
 * Hook for managing JavaScript node UI-related logic.
 * Handles custom buttons, intro steps, and node status.
 */
export function useJavascriptUI({
  openDialog,
  handleStartIntro,
  isProcessing,
  hasError,
  hasResult,
  hasExecutionResult,
}: UseJavascriptUIParams) {
  // Custom button configurations (icons will be handled in the component)
  const customButtons = useMemo(() => [
    {
      id: 'assign-users',
      icon: 'UserPlus',
      tooltip: 'Assign Users',
      onClick: () => openDialog('introDialog'), // Placeholder - should open user assignment
    },
    {
      id: 'config',
      icon: 'Settings',
      tooltip: 'Configuration',
      onClick: () => openDialog('configDialog'),
    },
    {
      id: 'data-settings',
      icon: 'Filter',
      tooltip: 'Data Settings',
      onClick: () => openDialog('dataSettingsDialog'),
    },
    {
      id: 'help',
      icon: 'HelpCircle',
      tooltip: 'Help',
      onClick: () => {
        const steps = [
          {
            element: '.command-selector',
            intro: 'Select the operation type: Generate, Enhance, or Debug.',
            position: 'bottom',
          },
          {
            element: '.code-editor',
            intro: 'Enter your JavaScript code here, or let the AI generate it.',
            position: 'bottom',
          },
          {
            element: '.requirements',
            intro: 'Describe what you want the JavaScript code to accomplish.',
            position: 'bottom',
          },
          {
            element: '.generate-button',
            intro: 'Click to generate or process the JavaScript code.',
            position: 'top',
          },
          {
            element: '.run-button',
            intro: 'Click to execute the JavaScript code.',
            position: 'top',
          },
        ]
        handleStartIntro(steps)
      },
    },
  ], [openDialog, handleStartIntro])

  // Intro.js options
  const introOptions = useMemo(() => ({
    nextLabel: 'Next',
    prevLabel: 'Previous',
    skipLabel: 'Skip',
    doneLabel: 'Done',
    showStepNumbers: true,
    exitOnOverlayClick: false,
    exitOnEsc: true,
    nextToDone: true,
    hidePrev: false,
    hideNext: false,
    showBullets: false,
  }), [])

  // Node status for the header
  const nodeStatus = useMemo(() => {
    if (isProcessing) return 'processing'
    if (hasError) return 'error'
    if (hasResult || hasExecutionResult) return 'success'
    return 'idle'
  }, [isProcessing, hasError, hasResult, hasExecutionResult])

  return {
    customButtons,
    introOptions,
    nodeStatus,
  }
}
